import { axiosInstance } from "@/lib/axios";
import queryClient from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

// Schemas & Types
export const partnerSchema = z.object({
  id: z.string(),
  name: z.string(),
  logo: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  createdAt: z.string().or(z.date()).optional(),
  updatedAt: z.string().or(z.date()).optional(),
});

export const partnerCreateSchema = z.object({
  name: z.string().min(1, "Name is required"),
  logo: z.string().url().optional().or(z.literal("")).nullable(),
  description: z.string().optional().nullable(),
  website: z.string().url().optional().or(z.literal("")).nullable(),
  productIds: z.array(z.string()).optional(),
});

export const partnerUpdateSchema = partnerCreateSchema.extend({
  id: z.string(),
  productIds: z.array(z.string()).optional(),
});

export const partnerWithProductsSchema = partnerSchema.extend({
  productIds: z.array(z.string()).optional(),
});

export type PartnerType = z.infer<typeof partnerSchema>;
export type PartnerCreateType = z.infer<typeof partnerCreateSchema>;
export type PartnerUpdateType = z.infer<typeof partnerUpdateSchema>;
export type PartnerWithProductsType = z.infer<typeof partnerWithProductsSchema>;

// API functions
export async function getPartnersFn() {
  return (
    await axiosInstance.get<{ success: boolean; data: PartnerType[] }>(
      "/api/partners",
    )
  ).data.data;
}

export async function getPartnerByIdFn(id: string) {
  return (
    await axiosInstance.get<{ success: boolean; data: PartnerType }>(
      `/api/partners/${id}`,
    )
  ).data.data;
}

export async function createPartnerFn(data: PartnerCreateType) {
  const payload = {
    ...data,
    logo: data.logo || null,
    website: data.website || null,
    productIds: data.productIds || [],
  };

  return (
    await axiosInstance.post<{ success: boolean; data: PartnerType }>(
      "/api/partners",
      payload,
    )
  ).data.data;
}

export async function updatePartnerFn(data: PartnerUpdateType) {
  const { id, productIds, ...updateData } = data;
  const payload = {
    ...updateData,
    logo: updateData.logo || null,
    website: updateData.website || null,
    productIds: productIds || [],
  };

  return (
    await axiosInstance.put<{ success: boolean; data: PartnerType }>(
      `/api/partners/${id}`,
      payload,
    )
  ).data.data;
}

export async function getPartnerProductsFn(partnerId: string) {
  return (
    await axiosInstance.get<{ success: boolean; data: string[] }>(
      `/api/partners/${partnerId}/products`,
    )
  ).data.data;
}

export async function deletePartnerFn(id: string) {
  return (
    await axiosInstance.delete<{ success: boolean }>(`/api/partners/${id}`)
  ).data;
}

// React Query wrapper
const Partner = {
  Get: {
    useQuery: (options = {}) =>
      useQuery({
        queryKey: ["Partners"],
        queryFn: () => getPartnersFn(),
        ...options,
      }),
  },

  GetById: {
    useQuery: (id: string, options = {}) =>
      useQuery({
        queryKey: ["Partner", id],
        queryFn: () => getPartnerByIdFn(id),
        enabled: !!id,
        ...options,
      }),
  },

  GetProducts: {
    useQuery: (partnerId: string, options = {}) =>
      useQuery({
        queryKey: ["Partner", partnerId, "products"],
        queryFn: () => getPartnerProductsFn(partnerId),
        enabled: !!partnerId,
        ...options,
      }),
  },

  Create: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: createPartnerFn,
        onSuccess: (data) => {
          toast.success(`Partner "${data.name}" created successfully`);
          queryClient.invalidateQueries({ queryKey: ["Partners"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to create partner";
          toast.error(message);
        },
        ...options,
      }),
  },

  Update: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: updatePartnerFn,
        onSuccess: (data) => {
          toast.success(`Partner "${data.name}" updated successfully`);
          queryClient.invalidateQueries({ queryKey: ["Partners"] });
          queryClient.invalidateQueries({ queryKey: ["Partner", data.id] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to update partner";
          toast.error(message);
        },
        ...options,
      }),
  },

  Delete: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: deletePartnerFn,
        onSuccess: () => {
          toast.success("Partner deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["Partners"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to delete partner";
          toast.error(message);
        },
        ...options,
      }),
  },
};

export default Partner;
